import React, { ChangeEvent, forwardRef } from "react";

import SearchIcon from "@mui/icons-material/Search";
import { CircularProgress, InputAdornment, TextFieldProps } from "@mui/material";

import { CloseIcon } from "@assets/svg";

import { IconButton } from "@/ui-kit";

import * as Styled from "./styles";
import { SearchTextFieldVariant } from "./types";

type TSearchTextField = Omit<TextFieldProps, "variant"> & {
  value: string;
  handleChange: (value: string, event?: ChangeEvent<HTMLInputElement>) => void;
  variant?: SearchTextFieldVariant;
  /** Show a spinner in place of the search icon while a search is running. */
  loading?: boolean;
  /** Nudge the leading icon down by N px (search + spinner share the slot). */
  iconOffsetY?: number;
};

export const SearchTextField = forwardRef<HTMLInputElement, TSearchTextField>((props, ref) => {
  const { value, handleChange, variant = "light", loading = false, iconOffsetY = 0, ...restProps } = props;

  return (
    <Styled.TextField
      fullWidth
      styleVariant={variant}
      ref={ref}
      value={value}
      onChange={(event: ChangeEvent<HTMLInputElement>) => {
        handleChange(event.target.value, event);
      }}
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">
            {value.length !== 0 && (
              <IconButton isTransparent onClick={() => handleChange("")} sx={{ "&&": { width: "24px", height: "24px", marginRight: "-4px" } }}>
                <CloseIcon width="10px" height="10px" />
              </IconButton>
            )}
          </InputAdornment>
        ),
        startAdornment: (
          <InputAdornment position="start">
            {/* Fixed-size slot so swapping search ↔ spinner never shifts the
                input text horizontally — only the icon inside changes. */}
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: 24,
                height: 24,
                flexShrink: 0,
              }}
            >
              {loading ? (
                // Stays perfectly centered in the slot (no icon nudge applied).
                <CircularProgress size={22} sx={{ color: "textColor.subtitle" }} />
              ) : (
                <SearchIcon sx={{ color: "textColor.subtitle", transform: iconOffsetY ? `translateY(${iconOffsetY}px)` : undefined }} />
              )}
            </span>
          </InputAdornment>
        ),
      }}
      {...restProps}
    />
  );
});
